import React, { useCallback, useMemo, useState } from "react";
import { Button, Card, CircularProgress, Grid, Icon, IconButton, Tooltip } from "@material-ui/core";
import { ListItemIcon, makeStyles, Menu, MenuItem, Typography } from "@material-ui/core";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MoreVert, AddOutlined, DeleteOutline, PeopleOutline, Lens } from "@material-ui/icons";
import Edit from "assets/icons/file-checklist-edit.svg";
import quotePageStyle from "features/quote/styles";
import { CHANGE_RESPONSIBLE, CLONE_QUOTE, DELETE_QUOTE } from "../mutations";
import { useClientQueryWithErrorHandling, useMutationWithErrorHandling } from "hooks/errorHandling";
import ActionsDialog from "../../common/components/ActionsDialog";
import { emptyArray, emptyString } from "constants/defaultValues";
import ChangeResponsibleContent from "./ChangeResponsibleContent";
import { addToast } from "components/notifications";
import { quoteStatus } from "constants/quoteStatus";
import QuoteDecisionContent from "features/quote/components/QuoteDecisionComponent";
import { zootDocDecisionColor } from "features/quote/constants";
import { useApolloClient } from "@apollo/client";
import Fragments from "features/quotes/fragments";
import { GET_ZOOT_DOCUMENT_DECISION_INFO_QUERY } from "features/quote/queries";
import { getTenant } from "providers/TenantProvider";
import { countryCodeConstants } from "features/common/constants";
import useUserData from "hooks/useUserData";
import { getRightToChangeResponsible } from "features/common/rights";
import { months } from "../constants";

const useStyles = makeStyles(quotePageStyle);

function Quote({ idx, quoteId, setQuoteList }) {
  const { countryCode } = getTenant();
  const { allowAutomatedDecision } = useUserData();
  const client = useApolloClient();
  const clientQuery = useClientQueryWithErrorHandling();
  const listQuoteItemCacheId = client.cache.identify({ __typename: "ListQuoteItem", quoteId: quoteId });
  const listQuoteItem = client.cache.readFragment({ id: listQuoteItemCacheId, fragment: Fragments.listQuoteItem });
  const quote = useMemo(() => ({ ...listQuoteItem, ...listQuoteItem?.quote }), [listQuoteItem]);

  const { userRights } = useUserData();
  const hasRightToChangeResponsible = useMemo(() => getRightToChangeResponsible(userRights), [userRights]);

  const quoteStyleProps = useMemo(
    () => ({ statusColor: quote?.statusColorCode, colorDecision: quote?.zootDocDecisionColor }),
    [quote]
  );
  const classes = useStyles(quoteStyleProps);

  const history = useHistory();
  const svgIcon = (
    <Icon className={classes.iconStyle}>
      <img src={Edit} alt="React Logo" />
    </Icon>
  );
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [decisionAnchorEl, setDecisionAnchorEl] = useState(null);
  const [actionsDialogProps, setActionsDialogProps] = useState({
    open: false,
    title: emptyString,
    message: emptyString,
    actions: emptyArray,
  });

  const handleCloseActionsDialog = useCallback(() => setActionsDialogProps((prev) => ({ ...prev, open: false })), []);

  const [deleteQuote, { loading: deleteQuoteLoading }] = useMutationWithErrorHandling(DELETE_QUOTE, {
    onCompleted: () => {
      setQuoteList((prev) => {
        const idx = prev.values.findIndex((x) => x.quoteId === quote.quoteId);
        prev.values.splice(idx, 1);
        return { ...prev };
      });

      addToast(t("DialogMessages.itemSuccessfullyDeleted"), "success");
    },
  });

  const [cloneQuote, { loading: cloneQuoteLoading }] = useMutationWithErrorHandling(CLONE_QUOTE, {
    onCompleted: (e) => {
      setQuoteList((prev) => ({ ...prev, values: [e.cloneQuote, ...prev.values] }));
      setActionsDialogProps({
        open: true,
        title: t("Quote.Information"),
        message: t("Quote.ContentPopupClone", { transactionId: e.cloneQuote.quote.transactionId }),
        actions: [
          {
            caption: t("General.OK"),
            handle: () => {
              return true;
            },
          },
          {
            caption: t("Quote.ViewQuote"),
            handle: () => {
              history.push({ pathname: `/quotes/${e.cloneQuote.quoteId}/assets` });
              return true;
            },
          },
        ],
      });
    },
  });

  const [changeResponsible, { loading: changeResponsibleLoading }] = useMutationWithErrorHandling(CHANGE_RESPONSIBLE, {
    onCompleted: () => {
      addToast(t("ToastNotifications.Success"), "success");
    },
  });

  const handleClickOptions = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setDecisionAnchorEl(null);
  }, []);

  const handleDeleteOption = useCallback(() => {
    setAnchorEl(null);
    setActionsDialogProps({
      open: true,
      title: t("General.Caution"),
      message: t("General.ConfirmDeleteItem"),
      actions: [
        {
          caption: t("General.ConfirmDeleteItemYes"),
          handle: () => {
            deleteQuote({ variables: { quoteId } });
            return true;
          },
        },
        {
          caption: t("General.ConfirmDeleteItemNo"),
          color: "secondary",
          handle: () => {
            return true;
          },
        },
      ],
    });
  }, [t, quoteId, deleteQuote]);

  const handleCloneOption = useCallback(() => {
    setAnchorEl(null);
    cloneQuote({ variables: { quoteId } });
  }, [cloneQuote, quoteId]);

  const handleChangeResponsibleOption = useCallback(() => {
    setAnchorEl(null);
    setActionsDialogProps({
      open: true,
      title: t("Quote.ChangeResponsible"),
      Content: ChangeResponsibleContent,
      actions: [
        {
          caption: t("Quote.ConfirmResponsibleChange"),
          handle: (state) => {
            changeResponsible({ variables: { quoteId, responsibleId: state.id } });
            return true;
          },
        },
        {
          caption: t("Quote.CancelResponsibleChange"),
          handle: () => {
            return true;
          },
        },
      ],
    });
  }, [changeResponsible, quoteId, t]);

  const handleOpenAssets = useCallback(() => {
    history.push(`/quotes/${quoteId}/assets`);
  }, [history, quoteId]);

  const handleOpenProducts = useCallback(() => {
    history.push(`/quotes/${quoteId}/products`);
  }, [history, quoteId]);

  const handleViewDecison = useCallback((event) => {
    setDecisionAnchorEl(event.currentTarget);
  }, []);

  const handleNextAction = useCallback(() => {
    history.push(`/quotes/${quoteId}`);
  }, [history, quoteId]);

  const getDateWithTranslatedMonth = useCallback(
    (lastActionAgo) => {
      const date = new Date(lastActionAgo);
      return lastActionAgo && !isNaN(date)
        ? date?.getDate() + " " + t(`General.${months[date?.getMonth()]}`) + " " + date?.getFullYear()
        : lastActionAgo;
    },
    [t]
  );

  return (
    <Card id={"quoteCard" + idx} className={classes.card}>
      <Grid container>
        <Grid item xs={12} md={2} className={classes.partnerSection}>
          <div className={classes.arrowTipItem}>
            <Typography className={classes.partnerName}>
              {quote.isEstimation ? quote.estimationName : quote.partnerName}
            </Typography>
            <Typography className={classes.reducedFont}>
              {quote.isEstimation ? t("General.Estimation") : quote.city}
            </Typography>
          </div>
        </Grid>
        <Grid item container xs={12} md={10} direction="column" className={classes.quoteInfo}>
          <Grid item container justifyContent="space-between" className={classes.quoteInfoUpperSection}>
            <Grid item xs={7}>
              <Grid container justifyContent="space-between" className={classes.quoteInfoUpperSectionLeft}>
                <Grid item xs={5} className={classes.quoteInfoAssetsSection}>
                  <Grid container justifyContent="space-around">
                    <Grid item>
                      <Typography className={classes.minFontLabel}>{t("Quote.Assets")}</Typography>
                      <Typography className={classes.reducedFont}>
                        {quote.assetNo} {t("Quote.Items")}
                      </Typography>
                      <Typography className={classes.minFontLabel}>{t("Quote.TotalNet")}</Typography>
                      <Typography className={classes.reducedFont}>
                        {t("DECIMAL2", { number: quote.totalAssetNetPrice })}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Button className={classes.buttonStyle} onClick={handleOpenAssets}>
                        {svgIcon}
                        {t("General.Edit")}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                {quote.productType && (
                  <Grid item xs={7} className={classes.quoteInfoProductSection}>
                    <Grid container justifyContent="space-around">
                      <Grid item xs={4}>
                        <Typography className={classes.minFontLabel}>{t("Quote.DownPayment")}</Typography>
                        <Typography className={classes.reducedFont}>
                          {t("DECIMAL2", { number: quote.downPayment })}
                        </Typography>
                        <Typography className={classes.minFontLabel}>{t("Quote.Term")}</Typography>
                        <Typography className={classes.reducedFont}>
                          {`${quote.period} ${t("EditProduct.Months")}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Button className={classes.buttonStyle} onClick={handleOpenProducts}>
                          {svgIcon}
                          {t("General.Edit")}
                        </Button>
                      </Grid>
                      <Grid item xs={4}>
                        <div className={classes.quoteItemPosition}>
                          <Typography className={classes.productType}>{quote.productType}</Typography>
                          <Typography className={classes.scheduleType}>
                            {quote.scheduleIsConfigurable &&
                              countryCode !== countryCodeConstants.china &&
                              countryCode !== countryCodeConstants.romania &&
                              t("Quote.SeasonalInstallments")}
                            {!quote.scheduleIsConfigurable && countryCode === countryCodeConstants.brazil && (
                              <Typography variant="body2" className={classes.scheduleType}>
                                {t("Product.InstallmentAmount")}
                              </Typography>
                            )}
                          </Typography>
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item>
              <Grid container justifyContent="space-between" className={classes.quoteInfoUpperSectionRight}>
                <Grid item>
                  <Grid container alignItems="center" justifyContent="flex-end" className={classes.decisionSection}>
                    <Grid item>
                      {allowAutomatedDecision && quote.zootDocDecisionFinished != null && (
                        <>
                          <Button onClick={handleViewDecison}>
                            <Lens className={classes.zootDocDecision} />
                          </Button>
                          <ActionsDialog
                            fullWidth={true}
                            open={Boolean(decisionAnchorEl)}
                            handleClose={handleClose}
                            title={t("Quote.Decision")}
                            initialState={{ quoteId: quote.quoteId }}
                            Content={QuoteDecisionContent}
                            actions={[
                              {
                                caption: t("General.Refresh"),
                                disabled:
                                  quote.zootDocDecisionFinished &&
                                  (quote.zootDocDecisionColor === zootDocDecisionColor.green ||
                                    quote.zootDocDecisionColor === zootDocDecisionColor.red),
                                handle: async () => {
                                  await clientQuery(GET_ZOOT_DOCUMENT_DECISION_INFO_QUERY, {
                                    variables: { quoteId },
                                    fetchPolicy: "network-only",
                                  });
                                  return true;
                                },
                              },
                              {
                                caption: t("General.Close"),
                                handle: () => {
                                  return true;
                                },
                              },
                            ]}
                          />
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container justifyContent="space-between" className={classes.nextActionSection}>
                    <Grid item xs container direction="column" justifyContent="space-around">
                      <Grid item>
                        <Typography className={classes.reducedFont}>{t("Quote.Next")}</Typography>
                      </Grid>
                      <Grid item>
                        {deleteQuoteLoading || cloneQuoteLoading || changeResponsibleLoading ? (
                          <CircularProgress size={20} color="primary" />
                        ) : (
                          <Button variant="outlined" className={classes.reducedFont} onClick={handleNextAction}>
                            {quote.nextActionName}
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Tooltip title={t("General.MoreActions")}>
                        <IconButton onClick={handleClickOptions} size="small">
                          <MoreVert />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        id="simple-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                      >
                        {quote.quoteStatusId > quoteStatus.pendingDecision.id && (
                          <MenuItem onClick={handleDeleteOption}>
                            <ListItemIcon>
                              <DeleteOutline fontSize="small" />
                            </ListItemIcon>
                            <Typography variant="inherit">{t("General.DeleteItem")}</Typography>
                          </MenuItem>
                        )}
                        <MenuItem onClick={handleCloneOption}>
                          <ListItemIcon>
                            <AddOutlined fontSize="small" />
                          </ListItemIcon>
                          <Typography variant="inherit">{t("Quote.NewFromCopy")}</Typography>
                        </MenuItem>
                        {hasRightToChangeResponsible && (
                          <MenuItem onClick={handleChangeResponsibleOption}>
                            <ListItemIcon>
                              <PeopleOutline fontSize="small" />
                            </ListItemIcon>
                            <Typography variant="inherit">{t("Quote.ChangeResponsible")}</Typography>
                          </MenuItem>
                        )}
                      </Menu>
                      <ActionsDialog
                        open={actionsDialogProps.open}
                        handleClose={handleCloseActionsDialog}
                        title={actionsDialogProps.title}
                        message={actionsDialogProps.message}
                        actions={actionsDialogProps.actions}
                        Content={actionsDialogProps.Content}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            container
            className={classes.quoteInfoLowerSection}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item>
              {t("Quote.TransactionId")}: {quote.transactionId}
            </Grid>
            <Grid item>
              {listQuoteItem?.oldActionName}: {getDateWithTranslatedMonth(listQuoteItem?.lastActionAgo)}
            </Grid>
            <Grid item>
              {t("Quote.Status")}: {quote.quoteStatusName}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
}

Quote.propTypes = {
  idx: PropTypes.number.isRequired,
  quoteId: PropTypes.number.isRequired,
  setQuoteList: PropTypes.func.isRequired,
};

export default Quote;
